/**
 * Iter 222 · Ambulance Dispatch Console
 * Live map + fleet list + mission queue + one-tap dispatch + SLA clocks.
 */
import React, { useEffect, useState, useMemo } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Ambulance, Siren, PlusCircle, ArrowClockwise, Play, Path,
  Clock, Warning, CheckCircle, X, MapPin,
} from "@phosphor-icons/react";

const AMB_TYPE_COLOR = {
  bls: "#0F766E", als: "#B91C1C", neonatal: "#4338CA", hearse: "#1F2937",
};
const STATUS_COLOR = {
  idle: "#10B981", dispatched: "#F59E0B", en_route_pickup: "#F59E0B",
  at_pickup: "#EF4444", en_route_hospital: "#EF4444", at_hospital: "#8B5CF6",
  returning: "#3B82F6", offline: "#6B7280", grounded: "#6B7280",
};

const MISSION_STATUS_STYLE = {
  sos:                "bg-[#FEE2E2] text-[#B91C1C] animate-pulse",
  dispatched:         "bg-[#FEF3C7] text-[#92400E]",
  en_route_pickup:    "bg-[#FEF3C7] text-[#92400E]",
  at_pickup:          "bg-[#FED7AA] text-[#C2410C]",
  en_route_hospital:  "bg-[#DBEAFE] text-[#1D4ED8]",
  at_hospital:        "bg-[#E0E7FF] text-[#4338CA]",
  returning:          "bg-[#CFFAFE] text-[#0E7490]",
  completed:          "bg-[#D4E0D1] text-[#1A3317]",
  cancelled:          "bg-[#F4C7B9] text-[#7A2E18]",
};

function ambIcon(color) {
  return L.divIcon({
    className: "custom-amb-icon",
    html: `<div style="width:22px;height:22px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>`,
    iconSize: [22, 22], iconAnchor: [11, 11],
  });
}

export default function AmbulanceDispatch() {
  const [tab, setTab] = useState("live");
  const [fleet, setFleet] = useState([]);
  const [stats, setStats] = useState({});
  const [missions, setMissions] = useState([]);
  const [buckets, setBuckets] = useState({});
  const [showNewSos, setShowNewSos] = useState(false);
  const [dispatchMission, setDispatchMission] = useState(null);

  const loadAll = async () => {
    try {
      const [f, m] = await Promise.all([
        api.get("/ambulance/admin/ambulances"),
        api.get("/ambulance/admin/missions?days=7"),
      ]);
      setFleet(f.data.ambulances || []); setStats(f.data.stats || {});
      setMissions(m.data.missions || []); setBuckets(m.data.by_status || {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    loadAll();
    const iv = setInterval(loadAll, 15000);
    return () => clearInterval(iv);
  }, []);

  const center = useMemo(() => {
    const withGps = fleet.find(a => a.current_lat && a.current_lng);
    return withGps ? [withGps.current_lat, withGps.current_lng] : [8.5241, 76.9366];
  }, [fleet]);

  const activeMissions = missions.filter(m => !["completed","cancelled"].includes(m.status));
  const sosQueue = missions.filter(m => m.status === "sos");

  return (
    <AppShell title="Ambulance Dispatch" subtitle="Command Centre"
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => setShowNewSos(true)}
            data-testid="amb-new-sos"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-[#B91C1C] text-white hover:bg-[#991B1B]">
            <Siren size={14}/> New SOS
          </button>
          <button onClick={loadAll} data-testid="amb-refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-[#E5E2D9] bg-white hover:bg-[#F7F6F2]">
            <ArrowClockwise size={14}/> Refresh
          </button>
        </div>
      }>
      <div className="space-y-4" data-testid="amb-dispatch-root">
        {/* Tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            {k:"total",       l:"Fleet",         v: stats.total ?? "—",       c:"#B91C1C", bg:"#FEE2E2"},
            {k:"idle",        l:"Idle",          v: stats.idle ?? "—",        c:"#10B981", bg:"#D1FAE5"},
            {k:"dispatched",  l:"En route",      v: stats.dispatched ?? "—",  c:"#F59E0B", bg:"#FEF3C7"},
            {k:"sos_queue",   l:"SOS queue",     v: sosQueue.length,          c:"#B91C1C", bg:"#FEE2E2"},
            {k:"active_mis",  l:"Active missions", v: activeMissions.length,  c:"#4338CA", bg:"#E0E7FF"},
          ].map(t => (
            <div key={t.k} data-testid={`amb-tile-${t.k}`}
                  className="p-4 bg-white rounded-lg border border-[#E5E2D9]">
              <div className="text-[24px] font-heading font-semibold" style={{color:t.c}}>{t.v}</div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{t.l}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-[#E5E2D9]">
          {["live","fleet","missions"].map(t => (
            <button key={t}
              data-testid={`amb-tab-${t}`}
              onClick={() => setTab(t)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 capitalize ${
                tab === t ? "border-[#B91C1C] text-[#B91C1C]" : "border-transparent text-[#5C5A55]"
              }`}>
              {t === "live" ? "Live Map" : t}
            </button>
          ))}
        </div>

        {tab === "live" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white rounded-lg border border-[#E5E2D9] overflow-hidden h-[520px]">
              <MapContainer center={center} zoom={12} style={{height:"100%", width:"100%"}}
                data-testid="amb-live-map">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
                {fleet.filter(a => a.current_lat && a.current_lng).map(a => (
                  <Marker key={a.id} position={[a.current_lat, a.current_lng]}
                    icon={ambIcon(STATUS_COLOR[a.status] || "#6B7280")}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-medium">{a.reg_no}</div>
                        <div>{a.amb_type?.toUpperCase()} · {a.kind}</div>
                        <div>Status: <b>{a.status}</b></div>
                        <div>{a.driver_name} · {a.driver_phone}</div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {activeMissions.filter(m => m.pickup_lat).map(m => (
                  <CircleMarker key={m.id} center={[m.pickup_lat, m.pickup_lng]}
                    radius={10} color="#B91C1C" fillColor="#FEE2E2" fillOpacity={0.6}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-medium">{m.mission_no}</div>
                        <div>{m.caller_name} · {m.caller_phone}</div>
                        <div>Status: {m.status}</div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
            <div className="bg-white rounded-lg border border-[#E5E2D9] p-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-3">SOS queue</div>
              {sosQueue.length === 0 ? (
                <div className="text-sm text-[#8A8780]">No pending SOS</div>
              ) : sosQueue.map(m => (
                <div key={m.id} data-testid={`sos-row-${m.mission_no}`}
                      className="p-3 mb-2 rounded-md border border-[#FCA5A5] bg-[#FEE2E2] animate-pulse">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-mono text-[11px] text-[#7F1D1D]">{m.mission_no}</div>
                      <div className="font-medium text-sm">{m.caller_name}</div>
                      <div className="text-[11px] text-[#5C5A55]">{m.caller_phone} · {m.amb_type_pref?.toUpperCase() || "ANY"}</div>
                    </div>
                    <button onClick={() => setDispatchMission(m)}
                      data-testid={`sos-dispatch-${m.mission_no}`}
                      className="px-2 py-1 text-xs rounded bg-[#B91C1C] text-white">Dispatch</button>
                  </div>
                </div>
              ))}
              <div className="mt-4 pt-4 border-t border-[#E5E2D9]">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">Status legend</div>
                <div className="space-y-1 text-xs">
                  {Object.entries(STATUS_COLOR).slice(0, 6).map(([k,c]) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full inline-block" style={{background:c}}/>
                      <span className="text-[#5C5A55] capitalize">{k.replace(/_/g," ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "fleet" && (
          <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F3EE] text-[#5C5A55] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Reg no.</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Kind</th>
                  <th className="text-left px-4 py-3">Driver</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Last ping</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0EDE4]">
                {fleet.map(a => (
                  <tr key={a.id} data-testid={`fleet-row-${a.reg_no}`}>
                    <td className="px-4 py-3 font-mono text-[12px]">{a.reg_no}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-full text-[11px] text-white"
                            style={{background: AMB_TYPE_COLOR[a.amb_type] || "#6B7280"}}>
                        {a.amb_type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#5C5A55] capitalize">{a.kind}</td>
                    <td className="px-4 py-3">
                      <div>{a.driver_name || "—"}</div>
                      <div className="text-[11px] text-[#8A8780]">{a.driver_phone || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="px-2 py-0.5 rounded-full text-[11px]"
                            style={{background: (STATUS_COLOR[a.status] || "#6B7280") + "22", color: STATUS_COLOR[a.status] || "#6B7280"}}>
                        {a.status?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[11px] text-[#8A8780]">
                      {a.last_ping_at ? new Date(a.last_ping_at).toLocaleTimeString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "missions" && (
          <div className="bg-white rounded-lg border border-[#E5E2D9] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F3EE] text-[#5C5A55] text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Mission#</th>
                  <th className="text-left px-4 py-3">Caller</th>
                  <th className="text-left px-4 py-3">Ambulance</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">SLA</th>
                  <th className="text-left px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0EDE4]">
                {missions.map(m => (
                  <tr key={m.id} data-testid={`mission-row-${m.mission_no}`}>
                    <td className="px-4 py-3 font-mono text-[12px]">{m.mission_no}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{m.caller_name}</div>
                      <div className="text-[11px] text-[#8A8780]">{m.caller_phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      {m.ambulance_reg_no ? (
                        <div>
                          <div className="font-mono text-[12px]">{m.ambulance_reg_no}</div>
                          <div className="text-[11px] text-[#8A8780]">{m.amb_type?.toUpperCase()}</div>
                        </div>
                      ) : (<span className="text-[11px] text-[#8A8780]">unassigned</span>)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] ${MISSION_STATUS_STYLE[m.status] || ""}`}>
                        {m.status?.replace(/_/g," ")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {m.sla?.breaches?.length > 0 ? (
                        <span className="px-2 py-0.5 rounded-full text-[11px] bg-[#FEE2E2] text-[#B91C1C]">
                          <Warning size={12} className="inline mr-1"/>
                          {m.sla.breaches.join(", ")}
                        </span>
                      ) : (
                        <span className="text-[11px] text-[#10B981]"><CheckCircle size={12} className="inline mr-1"/>On track</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {m.status === "sos" && (
                        <button onClick={() => setDispatchMission(m)}
                          data-testid={`mission-dispatch-${m.mission_no}`}
                          className="px-2 py-1 text-xs rounded bg-[#B91C1C] text-white">Dispatch</button>
                      )}
                      {m.ambulance_id && !["completed","cancelled"].includes(m.status) && (
                        <AdvanceMenu m={m} onAdvanced={loadAll}/>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showNewSos && <NewSosModal onClose={() => setShowNewSos(false)} onSaved={() => { setShowNewSos(false); loadAll(); }}/>}
      {dispatchMission && <DispatchModal mission={dispatchMission} onClose={() => setDispatchMission(null)} onDispatched={() => { setDispatchMission(null); loadAll(); }}/>}
    </AppShell>
  );
}

function AdvanceMenu({ m, onAdvanced }) {
  const [open, setOpen] = useState(false);
  const NEXT = {
    dispatched: "at_pickup", en_route_pickup: "at_pickup",
    at_pickup: "at_hospital", en_route_hospital: "at_hospital",
    at_hospital: "returning", returning: "completed",
  };
  const advance = async (status) => {
    try {
      await api.post(`/ambulance/admin/missions/${m.id}/advance`, { status });
      toast.success(`Marked ${status.replace(/_/g," ")}`);
      onAdvanced(); setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="inline-block relative">
      <button onClick={() => setOpen(o => !o)} data-testid={`advance-${m.mission_no}`}
        className="px-2 py-1 text-xs rounded bg-[#4338CA] text-white">→ Advance</button>
      {open && (
        <div className="absolute right-0 mt-1 bg-white shadow-lg rounded border border-[#E5E2D9] z-10 min-w-[160px]">
          {NEXT[m.status] && (
            <button onClick={() => advance(NEXT[m.status])}
              className="block w-full text-left px-3 py-2 text-xs hover:bg-[#F5F3EE]">
              → {NEXT[m.status].replace(/_/g," ")}
            </button>
          )}
          <button onClick={() => advance("completed")}
            className="block w-full text-left px-3 py-2 text-xs hover:bg-[#F5F3EE] text-[#10B981]">Complete</button>
          <button onClick={() => advance("cancelled")}
            className="block w-full text-left px-3 py-2 text-xs hover:bg-[#F5F3EE] text-[#B91C1C]">Cancel</button>
        </div>
      )}
    </div>
  );
}

function NewSosModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    caller_name:"", caller_phone:"", pickup_lat: 8.5241, pickup_lng: 76.9366,
    address:"", complaint:"", amb_type_pref:"bls", urgency:"emergency",
  });
  const set = (k,v) => setF(x => ({...x, [k]:v}));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.caller_name || !f.caller_phone) { toast.error("Name and phone required"); return; }
    try {
      setSaving(true);
      await api.post("/ambulance/admin/missions/sos", f);
      toast.success("SOS raised — dispatch queue updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={e => e.stopPropagation()}
            data-testid="amb-new-sos-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg text-[#B91C1C]"><Siren size={18} className="inline mr-1"/> New SOS</h2>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-1"><div className="text-xs mb-1">Caller name</div><input data-testid="sos-name" value={f.caller_name} onChange={e => set("caller_name", e.target.value)} className="w-full border rounded px-3 py-2"/></label>
          <label className="col-span-1"><div className="text-xs mb-1">Phone</div><input data-testid="sos-phone" value={f.caller_phone} onChange={e => set("caller_phone", e.target.value)} className="w-full border rounded px-3 py-2"/></label>
          <label className="col-span-1"><div className="text-xs mb-1">Pickup lat</div><input type="number" step="0.0001" value={f.pickup_lat} onChange={e => set("pickup_lat", Number(e.target.value))} className="w-full border rounded px-3 py-2"/></label>
          <label className="col-span-1"><div className="text-xs mb-1">Pickup lng</div><input type="number" step="0.0001" value={f.pickup_lng} onChange={e => set("pickup_lng", Number(e.target.value))} className="w-full border rounded px-3 py-2"/></label>
          <label className="col-span-2"><div className="text-xs mb-1">Address</div><input value={f.address} onChange={e => set("address", e.target.value)} className="w-full border rounded px-3 py-2"/></label>
          <label className="col-span-1"><div className="text-xs mb-1">Ambulance type</div>
            <select value={f.amb_type_pref} onChange={e => set("amb_type_pref", e.target.value)} className="w-full border rounded px-3 py-2">
              <option value="bls">BLS</option><option value="als">ALS</option>
              <option value="neonatal">Neonatal</option><option value="hearse">Hearse</option>
            </select>
          </label>
          <label className="col-span-1"><div className="text-xs mb-1">Urgency</div>
            <select value={f.urgency} onChange={e => set("urgency", e.target.value)} className="w-full border rounded px-3 py-2">
              <option value="emergency">Emergency</option><option value="urgent">Urgent</option>
            </select>
          </label>
          <label className="col-span-2"><div className="text-xs mb-1">Complaint</div>
            <textarea value={f.complaint} onChange={e => set("complaint", e.target.value)} rows={2} className="w-full border rounded px-3 py-2"/>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded">Cancel</button>
          <button data-testid="sos-save" disabled={saving} onClick={submit}
            className="px-3 py-1.5 text-sm bg-[#B91C1C] text-white rounded disabled:opacity-50">
            {saving ? "Raising…" : "Raise SOS"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DispatchModal({ mission, onClose, onDispatched }) {
  const [cands, setCands] = useState([]);
  const [picked, setPicked] = useState(null);
  const [dispatching, setDispatching] = useState(false);
  useEffect(() => {
    api.get(`/ambulance/admin/missions/${mission.id}/nearest?limit=5`)
      .then(r => setCands(r.data.candidates || []))
      .catch(e => toast.error(formatApiError(e)));
  }, [mission.id]);
  const doDispatch = async () => {
    if (!picked) { toast.error("Pick an ambulance"); return; }
    try {
      setDispatching(true);
      await api.post(`/ambulance/admin/missions/${mission.id}/dispatch`, { ambulance_id: picked });
      toast.success("Ambulance dispatched");
      onDispatched();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDispatching(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-md w-full p-5" onClick={e => e.stopPropagation()}
            data-testid="dispatch-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg">Dispatch — {mission.mission_no}</h2>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="text-sm text-[#5C5A55] mb-3">
          <div>Caller: <b>{mission.caller_name}</b> · {mission.caller_phone}</div>
          <div>Type pref: {mission.amb_type_pref?.toUpperCase() || "any"}</div>
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {cands.length === 0 && <div className="text-sm text-[#8A8780]">No idle ambulances match — try relaxing the type or wait for a returning one.</div>}
          {cands.map(c => (
            <label key={c.id} data-testid={`dispatch-cand-${c.reg_no}`}
                    className={`flex items-center justify-between p-3 border rounded cursor-pointer ${picked === c.id ? "border-[#B91C1C] bg-[#FEE2E2]" : "border-[#E5E2D9]"}`}>
              <div className="flex items-center gap-2">
                <input type="radio" name="cand" checked={picked === c.id} onChange={() => setPicked(c.id)}/>
                <div>
                  <div className="font-mono text-sm">{c.reg_no}</div>
                  <div className="text-[11px] text-[#8A8780]">{c.amb_type?.toUpperCase()} · {c.kind} · {c.driver_name}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{c.km} km</div>
                <div className="text-[11px] text-[#8A8780]">~{c.eta_min} min</div>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border rounded">Cancel</button>
          <button data-testid="dispatch-confirm" disabled={dispatching || !picked} onClick={doDispatch}
            className="px-3 py-1.5 text-sm bg-[#B91C1C] text-white rounded disabled:opacity-50">
            {dispatching ? "Dispatching…" : "Confirm dispatch"}
          </button>
        </div>
      </div>
    </div>
  );
}
